import React, { useState, useContext, useMemo, useRef, useCallback } from 'react';
import {
  Card,
  CardHeader,
  IconButton,
  Icon,
  TablePagination,
  Grid,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  CircularProgress,
  Tooltip
} from '@material-ui/core';
import { LanguageString } from '../Common';
import { CaseLoadFilterDialog } from './CaseLoadFilterDialog/CaseLoadFilterDialog';
import { StudentSortType } from '../../dto';
import { useCaseLoadMTSPeriods, useUserPreferences, useUpdateUserPreferences } from '../../hooks';
import { UserContext, SiteContext } from '../../contexts';
import { SortDirection } from 'tuapath-common/generated/schema';
import * as DTO from '../../dto';
import { CaseLoadOrder, CaseLoadOrderColumn } from './CaseLoadCells';
import { CaseLoadData } from './CaseLoadData';
import { getMonth, getYear, subMonths } from 'date-fns';

const currentDate = new Date();

const SearchInput: React.FC<{changeSearchTextOnTimeout: (val: string) => void, changeSearchTextNow: (val: string | undefined) => void}> = ({changeSearchTextOnTimeout, changeSearchTextNow}) => {
  const [searchText, setSearchText] = useState<string | undefined>(undefined);

  return <TextField
    id="CaseLoadSearch"
    label={<LanguageString groupName="GENERAL" resourceName="CASE_LOAD_SEARCH" defaultText="Search" />}
    value={searchText}
    onKeyPress={(event) => { if (event.key === 'Enter') changeSearchTextNow(searchText); }}
    onChange={event => {
      setSearchText(event.target.value);
      changeSearchTextOnTimeout(event.target.value);
    }}
    InputProps={{
      endAdornment: (
        <InputAdornment position="end">
          <Icon>search</Icon>
        </InputAdornment>
      )
    }}
  />;
};

export interface CaseLoadInnerProps {
  defaultFilters: DTO.SubmitFormInput | undefined;
}

export const CaseLoadInner: React.FC<CaseLoadInnerProps> = ({ defaultFilters }) => {
  const siteCtx = useContext(SiteContext);
  const userCtx = useContext(UserContext);
  const [updatePrefs] = useUpdateUserPreferences();
  const userPreferences = useUserPreferences({ variables: { userId: userCtx.user?.id ?? 0, keys: [DTO.PreferenceKey.CaseLoadFilters, DTO.PreferenceKey.CaseLoadOrder, DTO.PreferenceKey.CaseLoadPagination, DTO.PreferenceKey.CaseLoadAdvisor] } });

  const rowsPerPageOptions = [5, 10, 25, 50, 100];
  const [realSearchText, setRealSearchText] = useState<string>();
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [showClosedUsers, setShowClosedUsers] = useState(false);
  const [page, setPage] = useState(0);
  const [selectedPathTab, setSelectedPathTab] = useState<number | undefined>(siteCtx.site?.paths && siteCtx.site.paths.length > 0 ? siteCtx.site.paths[0].id : undefined);
  const [totalCount, setTotalCount] = useState(0);
  const filterAdvisorIds = useMemo(() => {
    const filterAdvisorIdsEntry = userPreferences.data?.userPreference?.find(entry => entry.key === DTO.PreferenceKey.CaseLoadAdvisor);
    if (filterAdvisorIdsEntry?.value) {
      return JSON.parse(filterAdvisorIdsEntry.value);
    }

    return [userCtx.user?.id ?? 0];
  }, [userPreferences.data]);
  const rowsPerPage = useMemo(() => {
    const rowsPerPageEntry = userPreferences.data?.userPreference?.find(entry => entry.key === DTO.PreferenceKey.CaseLoadPagination);
    if (rowsPerPageEntry?.value) {
      return JSON.parse(rowsPerPageEntry.value);
    }

    return rowsPerPageOptions[2];
  }, [userPreferences.data]);
  const filters = useMemo(() => {
    const filtersEntry = userPreferences.data?.userPreference?.find(entry => entry.key === DTO.PreferenceKey.CaseLoadFilters);
    if (filtersEntry?.value) {
      return JSON.parse(filtersEntry.value);
    }

    return defaultFilters ?? {};
  }, [userPreferences.data]);
  const order = useMemo(() => {
    const orderEntry = userPreferences.data?.userPreference?.find(entry => entry.key === DTO.PreferenceKey.CaseLoadOrder);
    if (orderEntry?.value) {
      return JSON.parse(orderEntry.value);
    }

    return {
      column: CaseLoadOrderColumn.Name,
      order: {
        orderBy: 'lastName',
        sortDirection: SortDirection.ASC,
        sortType: StudentSortType.StudentKey
      }
    };
  }, [userPreferences.data]);

  const prefUpdate = async (key: DTO.PreferenceKey, value: string) => {
    await updatePrefs({
      variables: {
        input: [{ key, value }]
      },
      refetchQueries: ['GetUserPreferences']
    });
  };

  const srchTimeout = useRef<NodeJS.Timeout>();

  const changeSearchTextOnTimeout = useCallback((newText: string) => {
    if (srchTimeout.current) clearTimeout(srchTimeout.current);

    srchTimeout.current = setTimeout(() => {
      setRealSearchText(newText);
      srchTimeout.current = undefined;
    }, 500);
  }, [setRealSearchText]);

  const changeSearchTextNow = useCallback((val: string | undefined) => {
    if (srchTimeout.current) clearTimeout(srchTimeout.current);

    setRealSearchText(val);
  }, [setRealSearchText]);

  const path = useMemo(() => siteCtx.site?.paths?.find(p => p.id === Number(selectedPathTab)), [siteCtx, selectedPathTab]);
  const programIds = useMemo(() => path?.programs?.map(p => p.id) ?? [], [path]);
  const periodResults = useCaseLoadMTSPeriods({ variables: { programIds } });

  const currentPeriod = useMemo(() => periodResults.data?.site.caseloadMtsPeriods.currentPeriod, [periodResults.data]);
  const previousPeriod = useMemo(() => periodResults.data?.site.caseloadMtsPeriods.previousPeriod, [periodResults.data]);
  const twoPeriodsAgo = useMemo(() => periodResults.data?.site.caseloadMtsPeriods.twoPeriodsAgo, [periodResults.data]);

  const loading = useMemo(() => {
    if (periodResults.loading) return true;
    if (!selectedPathTab) return true;
    if (siteCtx.site?.mtsPeriodsEnabled === true && !currentPeriod) return true;

    return false;
  }, [selectedPathTab, periodResults.loading, currentPeriod, twoPeriodsAgo, previousPeriod]);

  const toggleFilterDialog = useCallback(() => setFilterDialogOpen(prevState => !prevState), [setFilterDialogOpen]);

  const handleChangePage = useCallback((_: unknown, newPage: number) => setPage(newPage), [setPage]);

  const handleChangeRowsPerPage = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    await prefUpdate(DTO.PreferenceKey.CaseLoadPagination, JSON.stringify(Number(event.target.value)));
    setPage(0);
  }, [updatePrefs, setPage]);

  const clickedHeader = useCallback(async (column: CaseLoadOrderColumn, customProperty: string | undefined = undefined) => {
    const o: CaseLoadOrder = {
      column: column,
      order: {
        ...order.order,
        sortDirection: column === order.column ? (order.order.sortDirection === SortDirection.ASC ? SortDirection.DESC : SortDirection.ASC) : SortDirection.ASC
      }
    };

    const comp = o.order.orderBy.split('-');
    const mtsComp = comp[comp.length - 1];
    const nextMtsStatus = column === order.column ? (mtsComp === 'NOT_SUBMITTED' ? 'SUBMITTED' : (mtsComp === 'SUBMITTED' ? 'APPROVED' : 'NOT_SUBMITTED')) : 'NOT_SUBMITTED';

    const currentDate = new Date();
    const lastMonth = subMonths(currentDate, 1);
    const twoMonthsAgo = subMonths(currentDate, 2);
    const dates = {
      currentMonth: getMonth(currentDate) + 1,
      currentMonthYear: getYear(currentDate),
      lastMonth: getMonth(lastMonth) + 1,
      lastMonthYear: getYear(lastMonth),
      twoMonthsAgo: getMonth(twoMonthsAgo) + 1,
      twoMonthsAgoYear: getYear(twoMonthsAgo)
    };

    switch (column) {
      case 'Name':
        o.order.sortType = StudentSortType.StudentKey;
        o.order.orderBy = 'lastName';
        break;
      case 'TwoMonthsAgoMTS':
        o.order.sortType = StudentSortType.MTSStatus;
        if (siteCtx.site?.mtsPeriodsEnabled) {
          o.order.orderBy = `${twoPeriodsAgo?.id}-${dates.twoMonthsAgoYear}-${nextMtsStatus}`;
        } else {
          o.order.orderBy = `${dates.twoMonthsAgo}-${dates.twoMonthsAgoYear}-${nextMtsStatus}`;
        }        break;
      case 'TwoMonthsAgoDocs':
        o.order.sortType = StudentSortType.MTSDocs;
        if (siteCtx.site?.mtsPeriodsEnabled) {
          o.order.orderBy = `${twoPeriodsAgo?.id}-${dates.twoMonthsAgoYear}`;
        } else {
          o.order.orderBy = `${dates.twoMonthsAgo}-${dates.twoMonthsAgoYear}`;
        }
        break;
      case 'TwoMonthsAgoHours':
        o.order.sortType = StudentSortType.MTSHours;
        if (siteCtx.site?.mtsPeriodsEnabled) {
          o.order.orderBy = `${twoPeriodsAgo?.id}-${dates.twoMonthsAgoYear}`;
        } else {
          o.order.orderBy = `${dates.twoMonthsAgo}-${dates.twoMonthsAgoYear}`;
        }
        break;
      case 'TwoMonthsAgoWPRHours':
        o.order.sortType = StudentSortType.MTSWPRHours;
        if (siteCtx.site?.mtsPeriodsEnabled) {
          o.order.orderBy = `${twoPeriodsAgo?.id}-${dates.twoMonthsAgoYear}`;
        } else {
          o.order.orderBy = `${dates.twoMonthsAgo}-${dates.twoMonthsAgoYear}`;
        }
        break;
      case 'LastMonthMTS':
        o.order.sortType = StudentSortType.MTSStatus;
        if (siteCtx.site?.mtsPeriodsEnabled) {
          o.order.orderBy = `${previousPeriod?.id}-${dates.lastMonthYear}-${nextMtsStatus}`;
        } else {
          o.order.orderBy = `${dates.lastMonth}-${dates.lastMonthYear}-${nextMtsStatus}`;
        }
        break;
      case 'LastMonthMTSDocs':
        o.order.sortType = StudentSortType.MTSDocs;
        if (siteCtx.site?.mtsPeriodsEnabled) {
          o.order.orderBy = `${previousPeriod?.id}-${dates.lastMonthYear}`;
        } else {
          o.order.orderBy = `${dates.lastMonth}-${dates.lastMonthYear}`;
        }
        break;
      case 'LastMonthMTSHours':
        o.order.sortType = StudentSortType.MTSHours;
        if (siteCtx.site?.mtsPeriodsEnabled) {
          o.order.orderBy = `${previousPeriod?.id}-${dates.lastMonthYear}`;
        } else {
          o.order.orderBy = `${dates.lastMonth}-${dates.lastMonthYear}`;
        }
        break;
      case 'LastMonthMTSWPRHours':
        o.order.sortType = StudentSortType.MTSWPRHours;
        if (siteCtx.site?.mtsPeriodsEnabled) {
          o.order.orderBy = `${previousPeriod?.id}-${dates.lastMonthYear}`;
        } else {
          o.order.orderBy = `${dates.lastMonth}-${dates.lastMonthYear}`;
        }
        break;
      case 'CurrentMTS':
        o.order.sortType = StudentSortType.MTSStatus;
        if (siteCtx.site?.mtsPeriodsEnabled) {
          o.order.orderBy = `${currentPeriod?.id}-${nextMtsStatus}`;
        } else {
          o.order.orderBy = `${dates.currentMonth}-${dates.currentMonthYear}-${nextMtsStatus}`;
        }
        break;
      case 'CurrentMTSDocs':
        o.order.sortType = StudentSortType.MTSDocs;
        if (siteCtx.site?.mtsPeriodsEnabled) {
          o.order.orderBy = `${currentPeriod?.id}-${dates.currentMonthYear}`;
        } else {
          o.order.orderBy = `${dates.currentMonth}-${dates.currentMonthYear}`;
        }
        break;
      case 'CurrentMTSHours':
        o.order.sortType = StudentSortType.MTSHours;
        if (siteCtx.site?.mtsPeriodsEnabled) {
          o.order.orderBy = `${currentPeriod?.id}-${dates.currentMonthYear}`;
        } else {
          o.order.orderBy = `${dates.currentMonth}-${dates.currentMonthYear}`;
        }
        break;
      case 'CurrentMTSWPRHours':
        o.order.sortType = StudentSortType.MTSWPRHours;
        if (siteCtx.site?.mtsPeriodsEnabled) {
          o.order.orderBy = `${currentPeriod?.id}-${dates.currentMonthYear}`;
        } else {
          o.order.orderBy = `${dates.currentMonth}-${dates.currentMonthYear}`;
        }
        break;
      case 'MTSRequired':
        const mtsFormId = siteCtx.site?.caseLoadSettings?.mtsRequiredFormId?.toString();
        o.order.sortType = StudentSortType.DynamicForm;
        o.order.orderBy =  mtsFormId ? `${mtsFormId}-HasTrueAnswer` : '';
        break;
      case 'SupportServices':
        const supportFormId = siteCtx.site?.caseLoadSettings?.supportServicesFormId?.toString();
        o.order.sortType = StudentSortType.DynamicForm;
        o.order.orderBy = supportFormId ? `${supportFormId}-HasTrueAnswer` : '';
        break;
      case 'CoachMeeting':
        o.order.sortType = StudentSortType.CoachMeeting;
        break;
      case 'LastMTSActivity':
        o.order.sortType = StudentSortType.LastMTSActivity;
        break;
      case 'LastRoadmapInteraction':
        o.order.sortType = StudentSortType.LastRoadmapInteraction;
        break;
      case 'Custom':
        o.order.sortType = StudentSortType.CustomProperty;
        o.order.orderBy = customProperty ?? '';
        break;
    }

    await prefUpdate(DTO.PreferenceKey.CaseLoadOrder, JSON.stringify(o));
  }, [order, twoPeriodsAgo, currentDate, siteCtx.site?.caseLoadSettings?.mtsRequiredFormId, siteCtx.site?.caseLoadSettings?.supportServicesFormId]);

  return (
    <>
      <Grid container justify="center">
        <TablePagination
          rowsPerPageOptions={rowsPerPageOptions}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </Grid>

      <Card>
        <CardHeader
          title={
            <Grid container direction="row" spacing={4}>
              <Grid item>
                <LanguageString groupName="CASE_LOAD" resourceName="TITLE" defaultText="Case Load" />
              </Grid>
            </Grid>
          }
          action={
            <Grid container justify="center" alignItems="center" spacing={2}>
              <Grid item xs={9}>
                <SearchInput changeSearchTextNow={changeSearchTextNow} changeSearchTextOnTimeout={changeSearchTextOnTimeout} />
              </Grid>
              <Grid item xs={3}>
                <Tooltip title="Filters">
                  <IconButton role="button" aria-label="Filters" aria-hidden={false} onClick={toggleFilterDialog}>
                    <Icon>filter_list</Icon>
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>
          }
        />

        {siteCtx.site?.caseLoadSettings?.pathTabsEnabled && (
          <CardHeader
            title={
              <Tabs value={selectedPathTab} onChange={(e, newVal) => setSelectedPathTab(newVal)} aria-label="Path Tabs" variant="fullWidth">
                {siteCtx.site?.paths.map((path, i) => (
                  <Tab tabIndex={path.id} value={path.id} label={<LanguageString languageString={path.name} />} />
                ))}
              </Tabs>
            }
          />
        )}

        {loading ? (
          <Grid container justify='center' alignContent='center' alignItems='center' spacing={4}>
            <Grid item style={{ margin: 20 }}>
              <CircularProgress size={27} color="primary" />
            </Grid>
          </Grid>
        ): (
          <CaseLoadData
            filterAdvisorIds={filterAdvisorIds}
            realSearchText={realSearchText}
            rowsPerPage={rowsPerPage}
            page={page}
            currentPeriod={currentPeriod}
            previousPeriod={previousPeriod}
            twoPeriodsAgo={twoPeriodsAgo}
            selectedPathTab={selectedPathTab ?? 0}
            order={order}
            filters={filters}
            clickedHeader={clickedHeader}
            totalCount={totalCount}
            setTotalCount={setTotalCount}
          />
        )}


        {filterDialogOpen && (
          <CaseLoadFilterDialog
            open={filterDialogOpen}
            toggle={toggleFilterDialog}
            selectedAdvisorIds={filterAdvisorIds}
            changedSelectedAdvisorIds={async (ids) => {
              await prefUpdate(DTO.PreferenceKey.CaseLoadAdvisor, JSON.stringify(ids));
            }}
            showClosedUsers={showClosedUsers}
            changedShowClosedUsers={showClosed => {
              setShowClosedUsers(showClosedUsers);
            }}
          />
        )}
      </Card>
      <Grid container justify="center">
        <TablePagination
          rowsPerPageOptions={rowsPerPageOptions}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </Grid>
    </>
  );
};

export default CaseLoadInner;
